import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { Carousel } from 'primeng/carousel';
import { combineLatest, filter, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { AlertInterceptorComponent } from './components/alert-interceptor/alert-interceptor.component';
import { ButtonComponent } from './components/button/button.component';
import { FloatingInterceptorComponent } from './components/floating-interceptor/floating-interceptor.component';
import { FooterComponent } from './components/footer/footer.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { ToastComponent } from './components/toast/toast.component';
import { constants } from './constants';
import { User } from './models';
import { ErrorReportingService } from './services/error-reporting/error-reporting.service';
import { AlertConfig, InterceptorService } from './services/interceptor/interceptor.service';
import { LocalDataKey, LocalStorageService } from './services/local-storage/local-storage.service';
import { PlatformService } from './services/platform/platform.service';
import { RemoteConfigsService } from './services/remote-configs/remote-configs.service';
import { ToastService } from './services/toast/toast.service';
import { LocalUser, UsersService } from './services/users/users.service';
import { generateHash } from './utils/crypto.utils';
import { brevoIdentify } from './utils/enrollment.utils';

declare global {
  interface Window {
    Brevo?: {
      push(params: unknown[]): void;
    };
    dataLayer: {
      push(...params: unknown[]): void;
    };
    sib?: {
      email_id: string;
    };
  }
}

@Component({
  standalone: true,
  imports: [
    AlertInterceptorComponent,
    ButtonComponent,
    FloatingInterceptorComponent,
    FooterComponent,
    NavbarComponent,
    RouterModule,
    ToastComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  @ViewChild(AlertInterceptorComponent) alertInterceptorComponent: AlertInterceptorComponent;
  isPaymentGatewayRoute = false;
  shouldAskForCookiesAcceptance = false;
  showTestingEnvironmentAlert = false;
  protected url: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private errorReportingService: ErrorReportingService,
    private interceptorService: InterceptorService,
    private localStorageService: LocalStorageService,
    private platformService: PlatformService,
    private remoteConfigsService: RemoteConfigsService,
    private router: Router,
    private titleService: Title,
    private toastService: ToastService,
    private usersService: UsersService,
  ) {
    if (this.platformService.isBrowser()) {
      this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
        this.url = event.url;
        if (
          !event.url.startsWith('/ecommerce/search') &&
          !event.url.startsWith('/ecommerce/c') &&
          !event.url.startsWith('/desarrolladores/buscar')
        ) {
          window.scrollTo(0, 0);
        }
        this.isPaymentGatewayRoute = event.url.startsWith('/payment-gateway/');
        setTimeout(() => this.brevoPage(), 5000);
      });
    }
    Carousel.prototype.onTouchMove = () => {}; // this is for Carousel vertical scroll on mobile
  }

  async ngOnInit(): Promise<void> {
    if (this.platformService.isBrowser()) {
      const cookiesAcceptance = await firstValueFrom(this.localStorageService.get<boolean>(LocalDataKey.CookiesAcceptance));
      this.shouldAskForCookiesAcceptance = !cookiesAcceptance;
    }
    this.initialize();
    // This is to fix the tracking params from users that already had unflattened values
    const trackingParams = await firstValueFrom(this.localStorageService.get<Record<string, string | string[]>>(LocalDataKey.TrackingParams));
    for (const key in trackingParams) {
      if (Array.isArray(trackingParams[key])) {
        trackingParams[key] = (trackingParams[key] as string[]).join(',');
      }
    }
    this.localStorageService.set(LocalDataKey.TrackingParams, trackingParams);
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const keys = [
      'ad_id',
      'adgroup_id',
      'adset_id',
      'campaign_id',
      'gad_campaignid',
      'gad_source',
      'gbraid',
      'utm_campaign',
      'utm_content',
      'utm_id',
      'utm_medium',
      'utm_source',
      'utm_term',
      'wbraid',
    ];
    const extracted: Record<string, string> = {};
    for (const key of keys) {
      const value = queryParams[key];
      if (value) {
        extracted[key] = Array.isArray(value) ? value.join(',') : value;
      }
    }
    if (Object.keys(extracted).length > 0) {
      this.localStorageService.set(LocalDataKey.TrackingParams, extracted);
    }
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  acceptCookies(): void {
    this.localStorageService.set(LocalDataKey.CookiesAcceptance, true);
    this.shouldAskForCookiesAcceptance = false;
  }

  hideTestingEnvironmentAlert(): void {
    this.showTestingEnvironmentAlert = false;
    this.localStorageService.set(LocalDataKey.HideTestingEnvironmentAlert, true);
  }

  private brevoPage(): void {
    if (!window.Brevo || !window.Brevo.push) {
      return;
    }
    const pageName = this.router.url;
    const properties = {
      ma_title: this.titleService.getTitle(),
      ma_url: window.location.href,
      ma_path: this.router.url,
      ma_referrer: document.referrer,
    };
    window.Brevo.push([
      'page',
      pageName,
      properties,
    ]);
  }

  private initialize(): void {
    if (this.platformService.isServer()) {
      return;
    }
    if (this.localStorageService.getItem(LocalDataKey.HideTestingEnvironmentAlert)) {
      this.showTestingEnvironmentAlert = false;
    } else {
      this.showTestingEnvironmentAlert = environment.name !== 'prod';
    }
    combineLatest([
      this.usersService.getCurrentUser(),
      this.localStorageService.get<{ email: string; phoneNumber: string; phoneNumberMeta: string; }>(LocalDataKey.AnalyticsUserData),
    ]).subscribe({
      next: ([user, analyticsUserData]) => {
        if (user) {
          this.localStorageService.set<LocalUser>(LocalDataKey.LocalUser, {
            bankValidated: user.bankValidated,
            email: user.email,
            emailValidated: user.emailValidated,
            enrollmentStatus: user.enrollmentStatus,
            fatherLastName: user.fatherLastName,
            id: user.id,
            motherLastName: user.motherLastName,
            name: user.name,
            phoneNumber: user.phoneNumber,
            profileDataCompleted: user.profileDataCompleted,
          });
        } else {
          this.localStorageService.clear(LocalDataKey.LocalUser);
        }
        brevoIdentify(user);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.gtagIdentify(user, analyticsUserData);
      },
      error: (error) => this.errorReportingService.log('AppComponent.initialize()', 'get-current-user', error),
    });
    combineLatest([
      this.router.events,
      this.remoteConfigsService.get(),
    ]).subscribe({
      next: ([routerEvent, remoteConfig]) => {
        if (routerEvent instanceof NavigationEnd) {
          this.isPaymentGatewayRoute = routerEvent.url.startsWith('/payment-gateway/');
          if (
            !routerEvent.url.startsWith('/ecommerce/search') &&
            !routerEvent.url.startsWith('/ecommerce/c') &&
            !routerEvent.url.startsWith('/desarrolladores/buscar')
          ) {
            window.scrollTo(0, 0);
          }
          if (remoteConfig && !remoteConfig.paymentGatewayEnabled && this.isPaymentGatewayRoute) {
            this.toastService.showError({ title: 'Ha ocurrido un error en el proceso', description: 'En este momento no podemos completar esta acción, por favor intenta nuevamente mas tarde.' });
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.router.navigateByUrl('/');
          }
        }
        if (remoteConfig && remoteConfig.appVersionCode > constants.appVersionCode) {
          setTimeout(() => window.location.reload(), 5000);
        }
      },
      error: (error) => this.errorReportingService.log('AppComponent.initialize()', 'get-router-events-and-remote-config', error),
    });
    this.interceptorService.onAlert()
      .pipe(filter((config): config is AlertConfig => !!config))
      .subscribe({
        next: (config) => this.alertInterceptorComponent.open(config),
        error: (error) => this.errorReportingService.log('AppComponent.initialize()', 'alert-interceptor-open', error),
      });
  }

  private async gtagIdentify(user: User | null, analyticsUserData: { email: string; phoneNumber: string; phoneNumberMeta: string; } | null): Promise<void> {
    try {
      if (!window.dataLayer || !window.dataLayer.push) {
        return;
      }
      const hash: { email: string | undefined; phoneNumber: string | undefined; phoneNumberMeta: string | undefined; } = {
        email: undefined,
        phoneNumber: undefined,
        phoneNumberMeta: undefined,
      };
      if (user) {
        hash.email = await generateHash(user.email);
        hash.phoneNumber = user.phoneNumber ? await generateHash(user.phoneNumber) : undefined;
        hash.phoneNumberMeta = user.phoneNumber ? await generateHash(user.phoneNumber.replace('+', '')) : undefined;
      } else if (analyticsUserData) {
        hash.email = analyticsUserData.email;
        hash.phoneNumber = analyticsUserData.phoneNumber;
        hash.phoneNumberMeta = analyticsUserData.phoneNumberMeta;
      }
      if (!hash.email && !hash.phoneNumber && !hash.phoneNumberMeta) {
        window.dataLayer.push({
          user_id: undefined,
          telefono: undefined,
          telefono_meta: undefined,
        });
        return;
      }
      window.dataLayer.push({
        event: 'authentication',
        user_id: hash.email,
        telefono: hash.phoneNumber,
        telefono_meta: hash.phoneNumberMeta,
      });
    } catch (error) {
      this.errorReportingService.log('AppComponent.gtagIdentify()', 'identify-user-id', error);
    }
  }
}
